import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { 
  MessageCircle, X, Send, Loader2, Upload, FileSpreadsheet, 
  CheckCircle, AlertTriangle, Package, Languages, Megaphone,
  Trash2, ChevronRight, Bot, User, FileWarning, Shield, Scale, Link2
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { toast } from 'sonner';
import api from '@/lib/api';

// Supported languages
const SUPPORTED_LANGUAGES = ['tr', 'de', 'en', 'fr', 'it', 'ru', 'ar', 'fa'];

// Session states
const STATE_DISCLAIMER = 'disclaimer';
const STATE_PRICING = 'pricing';
const STATE_READY = 'ready';

// Quick actions for product management
const getQuickActions = (t) => [
  { id: 'upload', label: t('sellerChat.quick.uploadProducts', 'Ürün Yükle'), icon: Upload, intent: 'upload' },
  { id: 'categories', label: t('sellerChat.quick.checkCategories', 'Kategorileri Kontrol Et'), icon: Package, intent: 'categories' },
  { id: 'translate', label: t('sellerChat.quick.translateProducts', 'Ürünleri Çevir'), icon: Languages, intent: 'translate' },
  { id: 'promotion', label: t('sellerChat.quick.requestPromotion', 'Promosyon Talep Et'), icon: Megaphone, intent: 'promotion' },
  { id: 'remove', label: t('sellerChat.quick.removeProducts', 'Ürün Kaldır'), icon: Trash2, intent: 'remove' }
];

export const ProductAIChatbox = ({ isApproved = false }) => {
  const { t, i18n } = useTranslation();
  const currentLang = i18n.language;
  const [isOpen, setIsOpen] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [sessionState, setSessionState] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const [sellerName, setSellerName] = useState('');
  
  // Consent form state
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [consentAccepted, setConsentAccepted] = useState(false);
  
  // Pricing config state
  const [pricingConfig, setPricingConfig] = useState({
    priceType: 'NET',
    profitIncluded: false,
    shippingIncluded: false,
    shippingHandler: 'seller',
    profitShareAgreement: false,
    vatIncluded: true,
    vatRate: 18,
    vatPayer: 'seller',
    commissionRate: 0 // TurkishEmarket komisyon oranı (Brüt için)
  });
  
  // URL upload state
  const [urlInput, setUrlInput] = useState('');
  const [isUrlMode, setIsUrlMode] = useState(false);
  
  const scrollRef = useRef(null);
  const fileInputRef = useRef(null);

  const quickActions = getQuickActions(t);
  const isLanguageSupported = SUPPORTED_LANGUAGES.includes(currentLang);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  // Start session when chat is opened
  const startSession = async () => {
    try {
      setLoading(true);
      const response = await api.post('/seller/product-chat/start-session', null, {
        params: { language: currentLang }
      });
      
      setSessionId(response.data.session_id);
      setSessionState(response.data.state);
      setMessages([{ 
        role: 'assistant', 
        content: response.data.message, 
        type: 'disclaimer' 
      }]);
    } catch (error) {
      console.error('Failed to start session:', error);
      const errorMsg = error.response?.data?.detail || t('sellerChat.error', 'Bir hata oluştu.');
      toast.error(errorMsg);
      setMessages([{ role: 'assistant', content: errorMsg, type: 'error' }]);
    } finally {
      setLoading(false);
    }
  };

  // Handle chat open
  const handleOpen = () => {
    setIsOpen(true);
    if (!sessionId) {
      startSession();
    }
  };

  // Handle consent submission
  const handleConsentSubmit = async () => {
    if (!firstName.trim() || !lastName.trim()) {
      toast.error(t('sellerChat.nameRequired', 'Ad ve soyad gereklidir.'));
      return;
    }
    if (!consentAccepted) {
      toast.error(t('sellerChat.consentRequired', 'Koşulları kabul etmeniz gerekiyor.'));
      return;
    }

    try {
      setLoading(true);
      const response = await api.post('/seller/product-chat/consent', {
        session_id: sessionId,
        first_name: firstName.trim(),
        last_name: lastName.trim(),
        accepted: true,
        language: currentLang
      });
      
      setSessionState(response.data.state);
      setSellerName(response.data.seller_name);
      setMessages(prev => [
        ...prev,
        { role: 'user', content: `${firstName} ${lastName}, kabul ediyorum`, type: 'consent' },
        { role: 'assistant', content: response.data.message, type: 'pricing' }
      ]);
    } catch (error) {
      console.error('Consent error:', error);
      const errorDetail = error.response?.data?.detail;
      const errorMsg = typeof errorDetail === 'string' 
        ? errorDetail 
        : Array.isArray(errorDetail) 
          ? errorDetail.map(e => e.msg || e).join(', ')
          : t('sellerChat.error');
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  // Handle pricing config submission
  const handlePricingSubmit = async () => {
    try {
      setLoading(true);
      const formData = new FormData();
      formData.append('session_id', sessionId);
      formData.append('price_type', pricingConfig.priceType);
      formData.append('profit_included', String(pricingConfig.profitIncluded));
      formData.append('shipping_included', String(pricingConfig.shippingIncluded));
      formData.append('shipping_handler', pricingConfig.shippingHandler);
      formData.append('profit_share_agreement', String(pricingConfig.profitShareAgreement));
      formData.append('vat_included', String(pricingConfig.vatIncluded));
      formData.append('vat_rate', String(pricingConfig.vatRate || 0));
      formData.append('vat_payer', pricingConfig.vatPayer || 'seller');
      formData.append('commission_rate', String(pricingConfig.commissionRate || 0));
      formData.append('language', currentLang);

      // Use headers without Content-Type to let axios set it automatically for FormData
      const response = await api.post('/seller/product-chat/pricing-config', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      setSessionState(response.data.state);
      setMessages(prev => [
        ...prev,
        { role: 'user', content: t('sellerChat.pricingConfigured', 'Fiyatlandırma ayarları yapılandırıldı'), type: 'pricing_response' },
        { role: 'assistant', content: response.data.message, type: 'ready' }
      ]);
    } catch (error) {
      console.error('Pricing config error:', error);
      const errorDetail = error.response?.data?.detail;
      const errorMsg = typeof errorDetail === 'string' 
        ? errorDetail 
        : Array.isArray(errorDetail) 
          ? errorDetail.map(e => e.msg || e).join(', ')
          : t('sellerChat.error');
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  // Handle chat message
  const handleSend = async (e) => {
    e?.preventDefault();
    if (!input.trim() || loading || sessionState !== STATE_READY) return;

    const userMessage = input.trim();
    setInput('');
    setMessages(prev => [...prev, { role: 'user', content: userMessage }]);
    setLoading(true);

    try {
      const response = await api.post('/seller/product-chat', {
        message: userMessage,
        session_id: sessionId,
        language: currentLang
      });

      const { response: aiResponse, action } = response.data;
      
      setMessages(prev => [...prev, { role: 'assistant', content: aiResponse }]);

      if (action === 'request_file_upload') {
        fileInputRef.current?.click();
      }
    } catch (error) {
      console.error('Chat error:', error);
      setMessages(prev => [...prev, { 
        role: 'assistant', 
        content: error.response?.data?.detail || t('sellerChat.error', 'Bir hata oluştu.'),
        type: 'error'
      }]);
    } finally {
      setLoading(false);
    }
  };

  // Handle quick action
  const handleQuickAction = (action) => {
    const intentMessages = {
      upload: t('sellerChat.intents.upload', 'Ürün dosyası yüklemek istiyorum'),
      categories: t('sellerChat.intents.categories', 'Ürünlerimin kategorilerini kontrol etmek istiyorum'),
      translate: t('sellerChat.intents.translate', 'Ürünlerimi diğer dillere çevirmek istiyorum'),
      promotion: t('sellerChat.intents.promotion', 'Ürünlerim için promosyon talep etmek istiyorum'),
      remove: t('sellerChat.intents.remove', 'Ürün kaldırmak istiyorum')
    };
    
    setInput(intentMessages[action.intent] || action.label);
    setTimeout(() => handleSend({ preventDefault: () => {} }), 100);
  };

  // Handle URL upload
  const handleUrlUpload = async () => {
    if (!urlInput.trim()) {
      toast.error(t('sellerChat.urlRequired', 'Lütfen bir URL girin.'));
      return;
    }

    // Validate URL
    const urlPattern = /^(https?:\/\/)?([\da-z\.-]+)\.([a-z\.]{2,6})([\/\w \.-]*)*\/?$/i;
    if (!urlPattern.test(urlInput.trim())) {
      toast.error(t('sellerChat.invalidUrl', 'Geçersiz URL formatı. Lütfen doğru bir link girin.'));
      return;
    }

    setMessages(prev => [...prev, { 
      role: 'user', 
      content: `🔗 ${urlInput.trim()}`,
      type: 'url'
    }]);

    setIsUploading(true);
    setUploadProgress(0);

    try {
      const response = await api.post('/seller/product-chat/upload-url', {
        url: urlInput.trim(),
        session_id: sessionId,
        language: currentLang
      });

      const { message, warnings, prohibited_items, products_count } = response.data;
      let resultMessage = message;
      
      if (warnings && warnings.length > 0) {
        resultMessage += `\n\n⚠️ ${t('sellerChat.warnings', 'Uyarılar')}:\n${warnings.join('\n')}`;
      }

      if (prohibited_items && prohibited_items.length > 0) {
        resultMessage += `\n\n🚫 ${t('sellerChat.prohibitedItems', 'Yasaklı ürünler')}: ${prohibited_items.length} adet`;
      }

      setMessages(prev => [...prev, { role: 'assistant', content: resultMessage, type: 'upload_result' }]);
      toast.success(t('sellerChat.urlUploadSuccess', 'URL başarıyla işlendi!'));
      setUrlInput('');
      setIsUrlMode(false);
    } catch (error) {
      console.error('URL upload error:', error);
      const errorMsg = error.response?.data?.detail || t('sellerChat.urlUploadError', 'URL yükleme hatası.');
      setMessages(prev => [...prev, { role: 'assistant', content: `❌ ${errorMsg}`, type: 'error' }]);
      toast.error(errorMsg);
    } finally {
      setIsUploading(false);
      setUploadProgress(0);
    }
  };

  // Handle file upload
  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const fileExtension = file.name.split('.').pop()?.toLowerCase();
    const validExtensions = ['xml', 'csv', 'xls', 'xlsx'];
    
    if (!validExtensions.includes(fileExtension)) {
      toast.error(t('sellerChat.invalidFileType', 'Sadece XML, CSV ve Excel dosyaları kabul edilir.'));
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      toast.error(t('sellerChat.fileTooLarge', 'Dosya boyutu 10MB\'dan küçük olmalıdır.'));
      return;
    }

    setMessages(prev => [...prev, { 
      role: 'user', 
      content: `📁 ${file.name} (${(file.size / 1024).toFixed(1)} KB)`,
      type: 'file'
    }]);

    await uploadFile(file);
  };

  const uploadFile = async (file) => {
    setIsUploading(true);
    setUploadProgress(0);

    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('session_id', sessionId);
      formData.append('language', currentLang);

      const response = await api.post('/seller/product-chat/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (progressEvent) => {
          const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setUploadProgress(progress);
        }
      });

      const { message, warnings, prohibited_items } = response.data;
      let resultMessage = message;
      
      if (warnings && warnings.length > 0) {
        resultMessage += `\n\n⚠️ ${t('sellerChat.warnings', 'Uyarılar')}:\n${warnings.join('\n')}`;
      }

      if (prohibited_items && prohibited_items.length > 0) {
        resultMessage += `\n\n🚫 ${t('sellerChat.prohibitedItems', 'Yasaklı ürünler')}: ${prohibited_items.length} adet`;
      }

      setMessages(prev => [...prev, { role: 'assistant', content: resultMessage, type: 'upload_result' }]);
      toast.success(t('sellerChat.uploadSuccess', 'Dosya başarıyla işlendi!'));
    } catch (error) {
      console.error('Upload error:', error);
      const errorMsg = error.response?.data?.detail || t('sellerChat.uploadError', 'Yükleme hatası.');
      setMessages(prev => [...prev, { role: 'assistant', content: `❌ ${errorMsg}`, type: 'error' }]);
      toast.error(errorMsg);
    } finally {
      setIsUploading(false);
      setUploadProgress(0);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  // Render consent form
  const renderConsentForm = () => (
    <div className="p-4 space-y-4 bg-amber-50 border-t border-amber-200">
      <div className="flex items-center gap-2 text-amber-700">
        <Shield className="h-5 w-5" />
        <span className="font-medium">{t('sellerChat.enterYourName', 'Bilgilerinizi Girin')}</span>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <Input
          placeholder={t('sellerChat.firstName', 'Adınız')}
          value={firstName}
          onChange={(e) => setFirstName(e.target.value)}
          className="bg-white"
          data-testid="consent-first-name"
        />
        <Input
          placeholder={t('sellerChat.lastName', 'Soyadınız')}
          value={lastName}
          onChange={(e) => setLastName(e.target.value)}
          className="bg-white"
          data-testid="consent-last-name"
        />
      </div>
      <div className="flex items-start gap-2">
        <Checkbox 
          id="consent" 
          checked={consentAccepted}
          onCheckedChange={setConsentAccepted}
          data-testid="consent-checkbox"
        />
        <Label htmlFor="consent" className="text-sm text-amber-800 leading-tight">
          {t('sellerChat.acceptTerms', 'Yukarıdaki koşulları okudum ve kabul ediyorum.')}
        </Label>
      </div>
      <Button 
        onClick={handleConsentSubmit} 
        disabled={loading || !firstName || !lastName || !consentAccepted}
        className="w-full bg-amber-600 hover:bg-amber-700"
        data-testid="consent-submit-btn"
      >
        {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Scale className="h-4 w-4 mr-2" />}
        {t('sellerChat.acceptAndContinue', 'Kabul Et ve Devam Et')}
      </Button>
    </div>
  );

  // Render pricing config form
  const renderPricingForm = () => (
    <div className="p-4 space-y-4 bg-blue-50 border-t border-blue-200 max-h-80 overflow-y-auto">
      <div className="text-sm font-medium text-blue-800 mb-2">
        {t('sellerChat.pricingSettings', 'Fiyatlandırma Ayarları')}
      </div>
      
      {/* Price Type */}
      <div className="space-y-2">
        <Label className="text-xs font-medium">{t('sellerChat.priceType', 'Fiyat Türü')}</Label>
        <RadioGroup value={pricingConfig.priceType} onValueChange={(v) => setPricingConfig(p => ({...p, priceType: v}))}>
          <div className="flex items-center space-x-2">
            <RadioGroupItem value="NET" id="net" />
            <Label htmlFor="net" className="text-sm">NET ({t('sellerChat.netPrice', 'Son satış fiyatı')})</Label>
          </div>
          <div className="flex items-center space-x-2">
            <RadioGroupItem value="GROSS" id="gross" />
            <Label htmlFor="gross" className="text-sm">BRÜT ({t('sellerChat.grossPrice', 'İndirim/kar eklenecek')})</Label>
          </div>
        </RadioGroup>
      </div>

      {/* Profit Included */}
      <div className="flex items-center gap-2">
        <Checkbox 
          id="profitIncluded" 
          checked={pricingConfig.profitIncluded}
          onCheckedChange={(v) => setPricingConfig(p => ({...p, profitIncluded: v}))}
        />
        <Label htmlFor="profitIncluded" className="text-sm">{t('sellerChat.profitIncluded', 'Kar marjı fiyata dahil')}</Label>
      </div>

      {/* Shipping Included */}
      <div className="flex items-center gap-2">
        <Checkbox 
          id="shippingIncluded" 
          checked={pricingConfig.shippingIncluded}
          onCheckedChange={(v) => setPricingConfig(p => ({...p, shippingIncluded: v}))}
        />
        <Label htmlFor="shippingIncluded" className="text-sm">{t('sellerChat.shippingIncluded', 'Kargo fiyata dahil')}</Label>
      </div>

      {/* Shipping Handler */}
      <div className="space-y-2">
        <Label className="text-xs font-medium">{t('sellerChat.shippingHandler', 'Kargo Sorumlusu')}</Label>
        <RadioGroup value={pricingConfig.shippingHandler} onValueChange={(v) => setPricingConfig(p => ({...p, shippingHandler: v}))}>
          <div className="flex items-center space-x-2">
            <RadioGroupItem value="turkishemarket" id="tm-ship" />
            <Label htmlFor="tm-ship" className="text-sm">Turkishemarket.com</Label>
          </div>
          <div className="flex items-center space-x-2">
            <RadioGroupItem value="seller" id="seller-ship" />
            <Label htmlFor="seller-ship" className="text-sm">{t('sellerChat.seller', 'Satıcı (Ben)')}</Label>
          </div>
        </RadioGroup>
      </div>

      {/* Commission Rate - Only show for GROSS price type */}
      {pricingConfig.priceType === 'GROSS' && (
        <div className="space-y-2 p-3 bg-amber-50 rounded-lg border border-amber-200">
          <Label className="text-xs font-medium text-amber-800">
            {t('sellerChat.commissionRate', 'TurkishEmarket Komisyon/Kar Oranı (%)')}
          </Label>
          <Input 
            type="number" 
            min="0"
            max="100"
            step="0.5"
            value={pricingConfig.commissionRate} 
            onChange={(e) => setPricingConfig(p => ({...p, commissionRate: parseFloat(e.target.value) || 0}))}
            className="bg-white h-8"
            placeholder="Örn: 15"
            data-testid="commission-rate-input"
          />
          <p className="text-xs text-amber-700">
            {t('sellerChat.commissionNote', 'Brüt fiyatlara bu oran kadar komisyon eklenecektir.')}
          </p>
        </div>
      )}

      {/* Profit Share Agreement */}
      <div className="flex items-center gap-2">
        <Checkbox 
          id="profitShare" 
          checked={pricingConfig.profitShareAgreement}
          onCheckedChange={(v) => setPricingConfig(p => ({...p, profitShareAgreement: v}))}
        />
        <Label htmlFor="profitShare" className="text-sm">{t('sellerChat.profitShareAgreement', 'Turkishemarket.com ile kar payı anlaşması yapıldı')}</Label>
      </div>

      {/* VAT Included */}
      <div className="flex items-center gap-2">
        <Checkbox 
          id="vatIncluded" 
          checked={pricingConfig.vatIncluded}
          onCheckedChange={(v) => setPricingConfig(p => ({...p, vatIncluded: v}))}
        />
        <Label htmlFor="vatIncluded" className="text-sm">{t('sellerChat.vatIncluded', 'KDV fiyata dahil')}</Label>
      </div>

      {!pricingConfig.vatIncluded && (
        <div className="grid grid-cols-2 gap-2">
          <div>
            <Label className="text-xs">{t('sellerChat.vatRate', 'KDV Oranı (%)')}</Label>
            <Input 
              type="number" 
              value={pricingConfig.vatRate} 
              onChange={(e) => setPricingConfig(p => ({...p, vatRate: parseFloat(e.target.value) || 0}))}
              className="bg-white h-8"
            />
          </div>
          <div>
            <Label className="text-xs">{t('sellerChat.vatPayer', 'KDV\'yi kim öder?')}</Label>
            <select 
              value={pricingConfig.vatPayer}
              onChange={(e) => setPricingConfig(p => ({...p, vatPayer: e.target.value}))}
              className="w-full h-8 rounded-md border border-input bg-white px-2 text-sm"
            >
              <option value="seller">{t('sellerChat.seller', 'Satıcı')}</option>
              <option value="buyer">{t('sellerChat.buyer', 'Alıcı')}</option>
            </select>
          </div>
        </div>
      )}

      <Button 
        onClick={handlePricingSubmit} 
        disabled={loading}
        className="w-full bg-blue-600 hover:bg-blue-700"
        data-testid="pricing-submit-btn"
      >
        {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <CheckCircle className="h-4 w-4 mr-2" />}
        {t('sellerChat.savePricing', 'Ayarları Kaydet ve Başla')}
      </Button>
    </div>
  );

  // If seller is not approved
  if (!isApproved) {
    return (
      <div className="fixed bottom-6 right-6 z-50" data-testid="product-ai-chatbox">
        <button
          onClick={() => toast.warning(t('sellerChat.notApproved', 'Admin onayı gereklidir.'))}
          className="w-14 h-14 rounded-full bg-gray-400 text-white flex items-center justify-center shadow-lg cursor-not-allowed opacity-70"
          data-testid="product-chat-toggle-disabled"
        >
          <Bot className="h-6 w-6" />
        </button>
      </div>
    );
  }

  return (
    <div className="fixed bottom-6 right-24 z-50" data-testid="product-ai-chatbox">
      {isOpen && (
        <div className="absolute bottom-16 right-0 w-96 md:w-[450px] bg-white rounded-2xl shadow-2xl border overflow-hidden animate-slide-up">
          {/* Header */}
          <div className="bg-gradient-to-r from-purple-600 to-indigo-700 text-white p-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
                <Bot className="h-5 w-5" />
              </div>
              <div>
                <h3 className="font-medium">{t('sellerChat.title', 'Ürün Yükleme Asistanı')}</h3>
                <p className="text-xs text-white/80">
                  {sellerName ? `${sellerName}` : t('sellerChat.subtitle', 'AI destekli ürün yönetimi')}
                </p>
              </div>
            </div>
            <Button 
              variant="ghost" 
              size="icon" 
              onClick={() => setIsOpen(false)}
              className="text-white hover:bg-white/20"
              data-testid="product-chat-close-btn"
            >
              <X className="h-5 w-5" />
            </Button>
          </div>

          {/* Upload Progress */}
          {isUploading && (
            <div className="px-4 py-2 bg-purple-50 border-b">
              <div className="flex items-center gap-2 text-sm text-purple-700 mb-1">
                <Loader2 className="h-4 w-4 animate-spin" />
                {t('sellerChat.uploading', 'Dosya yükleniyor...')}
              </div>
              <Progress value={uploadProgress} className="h-2" />
            </div>
          )}

          {/* Messages */}
          <ScrollArea className="h-72 p-4" ref={scrollRef}>
            {messages.length === 0 && loading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-8 w-8 animate-spin text-purple-600" />
              </div>
            ) : (
              <div className="space-y-4">
                {messages.map((msg, i) => (
                  <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                    <div className="flex items-start gap-2 max-w-[90%]">
                      {msg.role === 'assistant' && (
                        <div className="w-8 h-8 rounded-full bg-purple-100 flex items-center justify-center flex-shrink-0">
                          <Bot className="h-4 w-4 text-purple-600" />
                        </div>
                      )}
                      <div
                        className={`rounded-2xl px-4 py-2 text-sm whitespace-pre-wrap ${
                          msg.role === 'user'
                            ? 'bg-purple-600 text-white rounded-br-none'
                            : msg.type === 'error'
                            ? 'bg-red-50 text-red-700 border border-red-200 rounded-bl-none'
                            : msg.type === 'disclaimer'
                            ? 'bg-amber-50 text-amber-900 border border-amber-200 rounded-bl-none'
                            : 'bg-gray-100 text-foreground rounded-bl-none'
                        }`}
                      >
                        {msg.content}
                      </div>
                      {msg.role === 'user' && (
                        <div className="w-8 h-8 rounded-full bg-purple-600 flex items-center justify-center flex-shrink-0">
                          <User className="h-4 w-4 text-white" />
                        </div>
                      )}
                    </div>
                  </div>
                ))}

                {/* Quick Actions - only show when ready */}
                {sessionState === STATE_READY && messages.length <= 3 && (
                  <div className="mt-4 space-y-2">
                    <p className="text-xs text-muted-foreground text-center mb-2">
                      {t('sellerChat.quickActions', 'Hızlı İşlemler')}
                    </p>
                    {quickActions.map((action) => (
                      <button
                        key={action.id}
                        onClick={() => handleQuickAction(action)}
                        className="block w-full text-left text-sm px-3 py-2 rounded-lg bg-purple-50 hover:bg-purple-100 transition-colors flex items-center gap-2"
                      >
                        <action.icon className="w-4 h-4 text-purple-600" />
                        {action.label}
                        <ChevronRight className="w-4 h-4 ml-auto text-purple-400" />
                      </button>
                    ))}
                  </div>
                )}

                {loading && sessionState === STATE_READY && (
                  <div className="flex justify-start mt-3">
                    <div className="flex items-center gap-2 bg-gray-100 rounded-2xl rounded-bl-none px-4 py-2">
                      <Loader2 className="h-4 w-4 animate-spin text-purple-600" />
                      <span className="text-sm text-muted-foreground">{t('sellerChat.thinking', 'Düşünüyorum...')}</span>
                    </div>
                  </div>
                )}
              </div>
            )}
          </ScrollArea>

          {/* Consent Form */}
          {sessionState === STATE_DISCLAIMER && renderConsentForm()}

          {/* Pricing Form */}
          {sessionState === STATE_PRICING && renderPricingForm()}

          {/* Chat Input - only when ready */}
          {sessionState === STATE_READY && (
            <form onSubmit={handleSend} className="p-4 border-t bg-gray-50">
              {/* URL Input Mode */}
              {isUrlMode ? (
                <div className="space-y-2">
                  <div className="flex gap-2">
                    <Input
                      value={urlInput}
                      onChange={(e) => setUrlInput(e.target.value)}
                      placeholder={t('sellerChat.urlPlaceholder', 'XML veya ürün linkinizi yapıştırın...')}
                      className="flex-1 rounded-full bg-white"
                      disabled={loading || isUploading}
                      data-testid="product-url-input"
                    />
                    <Button 
                      type="button"
                      size="icon" 
                      className="rounded-full bg-green-600 hover:bg-green-700" 
                      disabled={loading || isUploading || !urlInput.trim()}
                      onClick={handleUrlUpload}
                      data-testid="product-url-submit-btn"
                    >
                      {isUploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                    </Button>
                  </div>
                  <div className="flex justify-between items-center">
                    <p className="text-xs text-muted-foreground">
                      {t('sellerChat.urlFormats', 'Desteklenen: XML, CSV linkleri')}
                    </p>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => setIsUrlMode(false)}
                      className="text-xs text-purple-600 hover:text-purple-700"
                    >
                      {t('sellerChat.backToChat', '← Sohbete dön')}
                    </Button>
                  </div>
                </div>
              ) : (
                <>
                  <div className="flex gap-2">
                    <input
                      type="file"
                      ref={fileInputRef}
                      onChange={handleFileSelect}
                      accept=".xml,.csv,.xls,.xlsx"
                      className="hidden"
                      data-testid="product-file-input"
                    />
                    <Button
                      type="button"
                      size="icon"
                      variant="outline"
                      onClick={() => fileInputRef.current?.click()}
                      disabled={loading || isUploading}
                      className="flex-shrink-0"
                      title={t('sellerChat.uploadFile', 'Dosya Yükle')}
                      data-testid="product-upload-btn"
                    >
                      <FileSpreadsheet className="h-4 w-4" />
                    </Button>
                    <Button
                      type="button"
                      size="icon"
                      variant="outline"
                      onClick={() => setIsUrlMode(true)}
                      disabled={loading || isUploading}
                      className="flex-shrink-0"
                      title={t('sellerChat.uploadFromUrl', 'Link ile Yükle')}
                      data-testid="product-url-mode-btn"
                    >
                      <Link2 className="h-4 w-4" />
                    </Button>
                    
                    <Input
                      value={input}
                      onChange={(e) => setInput(e.target.value)}
                      placeholder={t('sellerChat.placeholder', 'Mesajınızı yazın...')}
                      className="flex-1 rounded-full bg-white"
                      disabled={loading || isUploading}
                      data-testid="product-chat-input"
                    />
                    <Button 
                      type="submit" 
                      size="icon" 
                      className="rounded-full bg-purple-600 hover:bg-purple-700" 
                      disabled={loading || isUploading || !input.trim()}
                      data-testid="product-chat-send-btn"
                    >
                      <Send className="h-4 w-4" />
                    </Button>
                  </div>
                  <p className="text-xs text-muted-foreground text-center mt-2">
                    {t('sellerChat.supportedFormats', 'Desteklenen formatlar: XML, CSV, Excel | Link ile de yükleyebilirsiniz')}
                  </p>
                </>
              )}
            </form>
          )}
        </div>
      )}

      {/* Toggle button */}
      <button
        onClick={handleOpen}
        className="w-14 h-14 rounded-full bg-gradient-to-r from-purple-600 to-indigo-700 text-white flex items-center justify-center shadow-lg hover:shadow-xl transition-all hover:scale-110"
        data-testid="product-chat-toggle-btn"
      >
        {isOpen ? <X className="h-6 w-6" /> : <Bot className="h-6 w-6" />}
      </button>
    </div>
  );
};

export default ProductAIChatbox;
